'use client';

import React from 'react';
import { LucideProps } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  IconClientRendererCds,
  IconClientRendererCdsType,
} from '@/components/cds/icon-client-renderer-cds';

/**
 * Props for the InputPrefixSuffixCds component.
 *
 * @interface InputPrefixSuffixCdsProps
 *
 * @property {string} [prefix] - Optional prefix text to be displayed before the input.
 * @property {string} [suffix] - Optional suffix text to be displayed after the input.
 * @property {IconClientRendererCdsType} [prefixIcon] - Optional icon component to be displayed before the input.
 * @property {IconClientRendererCdsType} [suffixIcon] - Optional icon component to be displayed after the input.
 * @property {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [prefixIconProps] - Optional props for the prefix icon.
 * @property {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} [suffixIconProps] - Optional props for the suffix icon.
 * @property {string} [prefixClassName] - Optional class name for the prefix element.
 * @property {string} [suffixClassName] - Optional class name for the suffix element.
 * @property {string} [prefixSuffixClassName] - Optional class name for both prefix and suffix elements.
 * @property {() => void} [onPrefixClick] - Optional click handler for the prefix element.
 * @property {() => void} [onSuffixClick] - Optional click handler for the suffix element.
 */
interface InputPrefixSuffixCdsProps {
  prefix?: string;
  suffix?: string;
  prefixIcon?: IconClientRendererCdsType;
  suffixIcon?: IconClientRendererCdsType;
  prefixIconProps?: Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>;
  suffixIconProps?: Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>;
  prefixClassName?: string;
  suffixClassName?: string;
  prefixSuffixClassName?: string;
  onPrefixClick?: () => void;
  onSuffixClick?: () => void;
}

/**
 * Props for the InputPrefixSuffixCdsComponent.
 *
 * @extends InputPrefixSuffixCdsProps
 * @extends React.InputHTMLAttributes<HTMLInputElement>
 */
interface InputPrefixSuffixCdsComponentProps
  extends InputPrefixSuffixCdsProps,
    React.InputHTMLAttributes<HTMLInputElement> {}

/**
 * `InputPrefixSuffixCds` is a React forward reference component that renders an input field with optional prefix and suffix elements.
 * The prefix and suffix can be either text or icons. The component dynamically calculates the padding for the input field
 * based on the length of the prefix and suffix to ensure proper spacing.
 *
 * The component also supports click handlers for the prefix and suffix elements.
 *
 * The component uses the `Input` component from `@/components/ui/input`.
 *
 * The component uses the `IsClientCds` component from `@/components/cds/is-client-cds` to conditionally render the prefix and suffix icons
 * as skeletons when the code is not running on the client side.
 *
 * @param {string} className - Additional classes for the input element.
 * @param {string} prefix - Text to be displayed as a prefix.
 * @param {string} suffix - Text to be displayed as a suffix.
 * @param {string} prefixClassName - Additional classes for the prefix element.
 * @param {string} suffixClassName - Additional classes for the suffix element.
 * @param {string} prefixSuffixClassName - Additional classes for both prefix and suffix elements.
 * @param {React.ComponentType} prefixIcon - Icon component to be displayed as a prefix.
 * @param {React.ComponentType} suffixIcon - Icon component to be displayed as a suffix.
 * @param {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} prefixIconProps - Props for the prefix icon.
 * @param {Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>} suffixIconProps - Props for the suffix icon.
 * @param {() => void} onPrefixClick - Click handler for the prefix element.
 * @param {() => void} onSuffixClick - Click handler for the suffix element.
 * @param {React.Ref<HTMLInputElement>} ref - Ref forwarded to the input element.
 * @param {React.ComponentProps<'input'>} props - Additional props for the input element.
 *
 * @returns {JSX.Element} The rendered input field with optional prefix and suffix elements.
 */
const InputPrefixSuffixCds = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & InputPrefixSuffixCdsComponentProps
>(
  (
    {
      className,
      prefix,
      suffix,
      prefixClassName,
      suffixClassName,
      prefixSuffixClassName,
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      prefixIconProps,
      suffixIconProps,
      onPrefixClick,
      onSuffixClick,
      ...props
    },
    ref
  ) => {
    // If this changes, the tailwind classes map needs to be updated
    const MAX_PREFIX_SUFFIX_LENGTH = 5;

    // Tailwind classes can't be dynamic, so we need to map them
    const TAILWIND_CLASSES_MAP: Record<string, string> = {
      '': '',
      'pl-1': 'pl-[1rem]',
      'pl-1.5': 'pl-[1.5rem]',
      'pl-2': 'pl-[2rem]',
      'pl-2.5': 'pl-[2.5rem]',
      'pl-3': 'pl-[3rem]',
      'pl-3.5': 'pl-[3.5rem]',
      'pl-4': 'pl-[4rem]',
      'pl-4.5': 'pl-[4.5rem]',
      'pl-5': 'pl-[5rem]',
      'pl-5.5': 'pl-[5.5rem]',
      'pl-6': 'pl-[6rem]',
      'pr-1': 'pr-[1rem]',
      'pr-1.5': 'pr-[1.5rem]',
      'pr-2': 'pr-[2rem]',
      'pr-2.5': 'pr-[2.5rem]',
      'pr-3': 'pr-[3rem]',
      'pr-3.5': 'pr-[3.5rem]',
      'pr-4': 'pr-[4rem]',
      'pr-4.5': 'pr-[4.5rem]',
      'pr-5': 'pr-[5rem]',
      'pr-5.5': 'pr-[5.5rem]',
      'pr-6': 'pr-[6rem]',
      'w-1': 'w-[1rem]',
      'w-1.5': 'w-[1.5rem]',
      'w-2': 'w-[2rem]',
      'w-2.5': 'w-[2.5rem]',
      'w-3': 'w-[3rem]',
      'w-3.5': 'w-[3.5rem]',
      'w-4': 'w-[4rem]',
      'w-4.5': 'w-[4.5rem]',
      'w-5': 'w-[5rem]',
      'w-5.5': 'w-[5.5rem]',
      'w-6': 'w-[6rem]',
    };

    const hasTextPrefix = typeof prefix === 'string' && !!prefix;
    const hasTextSuffix = typeof suffix === 'string' && !!suffix;
    const hasIconPrefix = !!PrefixIcon;
    const hasIconSuffix = !!SuffixIcon;

    /**
     * Calculates and returns the CSS classes for input padding and prefix/suffix width based on the presence and type of prefix/suffix.
     *
     * @param {boolean} hasTextPrefix - Indicates if there is a text prefix.
     * @param {boolean} hasIconPrefix - Indicates if there is an icon prefix.
     * @param {boolean} hasTextSuffix - Indicates if there is a text suffix.
     * @param {boolean} hasIconSuffix - Indicates if there is an icon suffix.
     * @param {string} prefix - The text prefix.
     * @param {string} suffix - The text suffix.
     * @param {React.ComponentType} PrefixIcon - The icon component for the prefix.
     * @param {React.ComponentType} SuffixIcon - The icon component for the suffix.
     * @returns {object} An object containing the calculated CSS classes:
     * - `prefixCalculatedInputClass`: The CSS class for input padding left based on the prefix.
     * - `prefixCalculatedClass`: The CSS class for width of the prefix.
     * - `suffixCalculatedInputClass`: The CSS class for input padding right based on the suffix.
     * - `suffixCalculatedClass`: The CSS class for width of the suffix.
     */
    const {
      prefixCalculatedInputClass,
      prefixCalculatedClass,
      suffixCalculatedInputClass,
      suffixCalculatedClass,
    } = React.useMemo(
      () => {
        let inputPaddingLeft = 0;
        let inputPaddingRight = 0;

        const getPadding = (text: string) => {
          // Get the total number if lowercase characters in the text
          const lowerLength = text.replace(/[^a-z]/g, '').length;
          // Get the total number of not lowercase characters in the text
          const otherLength = text.length - lowerLength;

          // Each lowercase character is 0.5rem and each other character is 1rem
          const length = lowerLength * 0.5 + otherLength;

          /**
           * Calculates the padding value based on the length of the input.
           * The padding is determined by taking the minimum of the input length and a maximum prefix/suffix length,
           * multiplying it by 10, rounding it to the nearest integer, and then dividing by 10.
           * Finally, 1 is added to the result.
           *
           * It must be a number between 1 and 6, inclusive, having a step of 0.5.
           *
           * @param length - The length of the input.
           * @returns The calculated padding value.
           */
          const padding = Math.round(Math.min(length, MAX_PREFIX_SUFFIX_LENGTH) * 10) / 10 + 1;

          return padding;
        };

        if (hasTextPrefix) {
          // Let's calculate the input's padding left based on the prefix length
          // about 0.5 rem per character + 1 rem for left and right padding of the prefix container
          inputPaddingLeft = getPadding(prefix);
        } else if (hasIconPrefix) {
          // The padding left for the input is 1.5 rem for the icon + 1 rem for left and right padding of the icon
          inputPaddingLeft = 1.5 + 1;
        }

        if (hasTextSuffix) {
          // Let's calculate the input's padding right based on the suffix length
          // about 0.5 rem per character + 1 rem for left and right padding of the suffix container
          inputPaddingRight = getPadding(suffix);
        } else if (hasIconSuffix) {
          // The padding right for the input is 1.5 rem for the icon + 1 rem for left and right padding of the icon
          inputPaddingRight = 1.5 + 1;
        }

        let prefixCalculatedInputClass = '';
        let prefixCalculatedClass = '';
        let suffixCalculatedInputClass = '';
        let suffixCalculatedClass = '';

        if (hasTextPrefix || hasIconPrefix) {
          prefixCalculatedInputClass = 'pl-' + inputPaddingLeft;
          // The max width of the prefix is the padding left minus 1
          prefixCalculatedClass = 'w-' + (inputPaddingLeft - 1);
        }

        if (hasTextSuffix || hasIconSuffix) {
          suffixCalculatedInputClass = 'pr-' + inputPaddingRight;
          // The max width of the suffix is the padding right minus 1
          suffixCalculatedClass = 'w-' + (inputPaddingRight - 1);
        }

        return {
          prefixCalculatedInputClass,
          prefixCalculatedClass,
          suffixCalculatedInputClass,
          suffixCalculatedClass,
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [prefix, suffix, PrefixIcon, SuffixIcon]
    );

    return (
      <div className="relative w-full">
        {hasTextPrefix && (
          <span
            className={cn(
              `absolute left-[0.5rem] top-1/2 -translate-y-1/2 text-center text-muted-foreground overflow-hidden text-clip`,
              TAILWIND_CLASSES_MAP[prefixCalculatedClass],
              prefixSuffixClassName,
              prefixClassName,
              onPrefixClick ? 'cursor-pointer' : ''
            )}
            onClick={onPrefixClick}
          >
            {prefix}
          </span>
        )}
        {!hasTextPrefix && hasIconPrefix && (
          <IconClientRendererCds
            {...prefixIconProps}
            icon={PrefixIcon}
            className={cn(
              'absolute left-[0.5rem] top-1/2 -translate-y-1/2 text-muted-foreground size-[1.5rem]',
              TAILWIND_CLASSES_MAP[prefixCalculatedClass],
              prefixSuffixClassName,
              prefixClassName,
              onPrefixClick ? 'cursor-pointer' : ''
            )}
            onClick={onPrefixClick}
          />
        )}
        <Input
          className={cn(
            TAILWIND_CLASSES_MAP[prefixCalculatedInputClass],
            TAILWIND_CLASSES_MAP[suffixCalculatedInputClass],
            className
          )}
          ref={ref}
          {...props}
        />
        {hasTextSuffix && (
          <span
            className={cn(
              `absolute right-[0.5rem] top-1/2 -translate-y-1/2 text-center text-muted-foreground overflow-hidden text-clip`,
              TAILWIND_CLASSES_MAP[suffixCalculatedClass],
              prefixSuffixClassName,
              suffixClassName,
              onSuffixClick ? 'cursor-pointer' : ''
            )}
            onClick={onSuffixClick}
          >
            {suffix}
          </span>
        )}
        {!hasTextSuffix && hasIconSuffix && (
          <IconClientRendererCds
            {...suffixIconProps}
            icon={SuffixIcon}
            className={cn(
              'absolute right-[0.5rem] top-1/2 -translate-y-1/2 text-muted-foreground size-[1.5rem]',
              TAILWIND_CLASSES_MAP[suffixCalculatedClass],
              prefixSuffixClassName,
              suffixClassName,
              onSuffixClick ? 'cursor-pointer' : ''
            )}
            onClick={onSuffixClick}
          />
        )}
      </div>
    );
  }
);
InputPrefixSuffixCds.displayName = 'InputPrefixSuffixCds';

export { InputPrefixSuffixCds };
export type { InputPrefixSuffixCdsProps, InputPrefixSuffixCdsComponentProps };
